/**
 * Get the backend URL for image serving
 * Uses NEXT_PUBLIC_BACKEND_URL environment variable
 */
export function getBackendUrl(): string {
    // ✅ Server-side (API routes, getServerSideProps)
    if (typeof window === "undefined") {
        if (!process.env.BACKEND_URL) {
            throw new Error("BACKEND_URL must be set on the server");
        }
        return process.env.BACKEND_URL;
    }

    // ✅ Client-side (browser)
    if (window.__ENV__?.NEXT_PUBLIC_BACKEND_URL) {
        return window.__ENV__.NEXT_PUBLIC_BACKEND_URL;
    }

    if (process.env.NEXT_PUBLIC_BACKEND_URL) {
        return process.env.NEXT_PUBLIC_BACKEND_URL;
    }

    throw new Error("NEXT_PUBLIC_BACKEND_URL not available in browser");
}

/**
 * Get the full URL for an image stored in the backend
 * @param folder - The folder (maps or photospheres)
 * @param filename - The filename or path within the folder
 */
export function getImageUrl(folder: "maps" | "photospheres", filename: string): string {
    const baseUrl = getBackendUrl();
    return `${baseUrl}/images/${folder}/${filename}`;
}

/**
 * Get the full URL for a map image
 * @param imagePath - The image path/filename
 */
export function getMapImageUrl(imagePath: string): string {
    return getImageUrl("maps", imagePath);
}

/**
 * Get the full URL for a photosphere image
 * @param photosphereId - The photosphere ID
 * @param filename - The image filename (e.g., panorama_medium.webp, thumbnail.webp)
 */
export function getPhotosphereImageUrl(photosphereId: string, filename: string): string {
    return getImageUrl("photospheres", `${photosphereId}/${filename}`);
}

/**
 * Get the thumbnail URL for a photosphere
 * @param photosphereId - The photosphere ID
 */
export function getPhotosphereThumbnailUrl(photosphereId: string): string {
    return getPhotosphereImageUrl(photosphereId, "thumbnail.webp");
}
